// Dub instruments (docs/AUDIO_DESIGN.md §12), used only by dub.js. The "desk" is one tape echo and one spring per track,
// persistent nodes that the skank, the cross-stick / snare and the melodica send into; a throw (one chop or one backbeat
// sent in at full level while the loop's feedback swells) runs it into a long tail. The skank is the chord instrument
// ("Chords only" plays it): a muted clean guitar chop and a drawbar organ (its right hand on the chops, its left hand
// bubbling on the "and"s, held chords in the intro and the break), one oscillator + one gain a note per layer. The bass
// (a round, softly saturated sine + triangle sub), the one-drop kit and the melodica are persistent and gated with
// setTargetAtTime.
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { swing } from '../rhythm.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';

// Level knobs (the harness zeroes them to measure one part alone).
export const DUB_LEVELS = { skank: 1, bass: 1, kit: 1, lead: 1, echo: 1 };
/** The swung 8th: an "and" (the 2nd 8th of a beat) sits AND of a 16th late, plus the engine's swing of a fractional step
 *  (rhythm.js swing), so the comping cells, the bass line and the kit (which moves its "and" hits by the same amount) agree. */
export const AND = 0.42;

const lerp = (a, b, x) => a + (b - a) * x;
const mod = (n, m) => ((n % m) + m) % m;
/** soft saturation y = tanh(kx) / k: unity gain for small signals (safe inside a feedback loop), rounder peaks */
const satCurve = k => { const c = new Float32Array(1025); for (let i = 0; i < 1025; i++) c[i] = Math.tanh(k * (i / 512 - 1)) / k; return c; };
const shaper = (ac, k, lite) => { const w = ac.createWaveShaper(); w.curve = satCurve(k); w.oversample = lite ? 'none' : '2x'; return w; };

/* ---------------- periodic waves (one set per context) ---------------- */
// guitar: a string plucked a fifth of the way along (|sin(πn·.19)|), rolling off as n^−1.25 (20 harmonics) · organ: drawbars 8' 4' 2⅔' 2'
// (+ a little 1⅗' and 1') · melodica: a 36 % pulse (sin(πn·.36)/n), softened above the 14th harmonic
const WAVES = new WeakMap();
function waves(ac) {
  if (WAVES.has(ac)) return WAVES.get(ac);
  const make = f => { const n = 32, im = new Float32Array(n); for (let k = 1; k < n; k++) im[k] = f(k); return ac.createPeriodicWave(new Float32Array(n), im); };
  const organ = [0, 1, 0.55, 0.32, 0.2, 0.06, 0.08, 0, 0.04];
  const w = {
    gtr: make(k => (k > 20 ? 0 : Math.abs(Math.sin(Math.PI * k * 0.19)) / Math.pow(k, 1.25))),
    organ: make(k => organ[k] || 0),
    reed: make(k => Math.sin(Math.PI * k * 0.36) / k / (1 + Math.pow(k / 14, 2))),
  };
  WAVES.set(ac, w);
  return w;
}

/* ---------------- the desk: tape echo + spring, one of each per track ---------------- */
/** p = plan.dub.desk: { steps (3 = dotted 8th | 4 = quarter), fb, fbThrow, lp, hp, drive, wobHz, wobMs, pan, wet,
 *  spring: { ms, fb, lp, wet } }.
 *  echo: echoIn (HP p.hp) → delay (steps × stepDur, its time wobbling ±wobMs at wobHz: the tape) → LP p.lp → tape sat (tanh
 *  k p.drive, unity for small signals) → fb p.fb back into the delay, so each repeat is darker, thinner and softer; the loop
 *  → wet → pan p.pan → out, and the reverb (.2).
 *  spring: springIn (HP 280) → delay p.spring.ms → allpasses 1100 / 2600 Hz (Q .5: the drip's chirp) → LP p.spring.lp → fb
 *  p.spring.fb back into the delay; the loop → wet → out.
 *  throw(t): the echo's feedback rises to fbThrow (τ 40 ms) and eases back from 1.25 bars later (τ .45 s). */
export function createDesk(ac, out, send, p, { registry, tag, t0, stepDur, lite = false }) {
  const nodes = [], N = x => (nodes.push(x), x);
  const echoIn = N(filter(ac, 'highpass', p.hp, 0.6)), dly = N(ac.createDelay(2)), lp = N(filter(ac, 'lowpass', p.lp, 0.6));
  const sat = N(shaper(ac, p.drive, lite)), fb = N(gain(ac, p.fb)), wet = N(gain(ac, p.wet * DUB_LEVELS.echo)), pan = N(panner(ac, p.pan));
  const wob = N(osc(ac, 'sine', p.wobHz)), wobG = N(gain(ac, p.wobMs / 1000)), toRev = N(gain(ac, 0.2));
  dly.delayTime.value = Math.min(1.9, p.steps * stepDur);
  echoIn.connect(dly); dly.connect(lp); lp.connect(sat); sat.connect(fb); fb.connect(dly);
  sat.connect(wet); wet.connect(pan); pan.connect(out); pan.connect(toRev); toRev.connect(send);
  wob.connect(wobG); wobG.connect(dly.delayTime);
  const s = p.spring, springIn = N(filter(ac, 'highpass', 280, 0.6)), sD = N(ac.createDelay(0.1));
  const aps = [1100, 2600].map(f => N(filter(ac, 'allpass', f, 0.5))), sLP = N(filter(ac, 'lowpass', s.lp, 0.6));
  const sFb = N(gain(ac, s.fb)), sWet = N(gain(ac, s.wet));
  sD.delayTime.value = s.ms / 1000;
  springIn.connect(sD); sD.connect(aps[0]); aps[0].connect(aps[1]); aps[1].connect(sLP);
  sLP.connect(sFb); sFb.connect(sD); sLP.connect(sWet); sWet.connect(out);
  const P = persistent(registry, tag, [wob], nodes, t0), barDur = 16 * stepDur;
  return {
    echoIn, springIn,
    throw(t) {
      hold(fb.gain, t);
      fb.gain.setTargetAtTime(p.fbThrow, t, 0.04);
      fb.gain.setTargetAtTime(p.fb, t + 1.25 * barDur, 0.45);
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- the skank: muted guitar chop + drawbar organ ---------------- */
/** p = plan.dub.skank: { mode ('guitar' | 'organ' | 'both'), vel, drive, gLp, gDecay, pluck, gPan, oLp, oPan,
 *  chorus: { rate, ms }, echo: { g, o }, spring, pad: { atk, rel } }. desk = createDesk(…).
 *  What a chord call is follows from its notes and length: a chop (≥ 2 notes, < 2.6 steps) is the guitar and/or the organ's
 *  right hand by p.mode; a single note is the organ's left hand (the bubble); anything held longer is an organ chord.
 *  guitar (per note, 1 oscillator + 1 gain): the plucked-string wave, ±3 ¢, a 2 ms attack to .52·v^1.2 (×1.85 when the
 *  guitar chops alone, ×.95 with the organ), ringing (τ p.gDecay, .16–.26 s) until the fretting hand mutes it at the note's
 *  end (τ 12 ms). Bus: amp (tanh k p.drive 1.4–2, unity for small signals: the strum's attack breaks up a little) → peaking
 *  1.6 kHz +2.5 dB → LP p.gLp (Q .7), plucked open ×p.pluck (1.2–1.6) on each chop (τ 50 ms back) → pan p.gPan; a noise
 *  scratch (BP 2.2 kHz, .12v, τ 12 ms) on each chop. Sends: echo p.echo.g, spring p.spring, reverb .22.
 *  organ (per note): the drawbar wave, ±4 ¢, .61·v^1.3 (chops ×.55 with the guitar, bubble notes ×.85, held chords ×.36):
 *  a chop / bubble note in τ 2.5 ms, settling to .78 (τ .25 s), off τ 20 ms; a held chord in τ 6 ms … p.pad.atk (the longer
 *  the note, the slower), settling to .9, off τ p.pad.rel (or the engine's release if longer). A held note struck again
 *  where it ends ties (no re-attack). Bus: tanh k 1.3 → LP p.oLp → pan p.oPan, and a vibrato-chorus (a 4 ms delay swept
 *  ±p.chorus.ms at p.chorus.rate, wet .5, panned across; not in lite). Sends: echo p.echo.o, reverb .22.
 *  A throw: a chop at exactly velocity 1 (the style's "throw" cell asks for more than 1 and the engine clamps it; no other
 *  cell gets there) plays at the track's chop velocity p.vel with both echo sends at .95 for its length, and swells the echo. */
export function createSkank(ac, out, send, desk, p, { registry, tag, cap = 16, r = Math.random, t0, stepDur, log = null, lite = false }) {
  const nodes = [], N = x => (nodes.push(x), x), W = waves(ac), L = DUB_LEVELS.skank;
  const gtrOn = p.mode !== 'organ', orgOn = p.mode !== 'guitar', gMix = p.mode === 'both' ? 0.95 : 1.85, oMix = p.mode === 'both' ? 0.55 : 1;
  // guitar tone → pan → out · echo · spring · reverb
  const gIn = N(shaper(ac, p.drive, lite)), gPk = N(filter(ac, 'peaking', 1600, 0.9, 2.5)), gLP = N(filter(ac, 'lowpass', p.gLp, 0.7));
  const gPan = N(panner(ac, p.gPan)), gEcho = N(gain(ac, p.echo.g)), gSpr = N(gain(ac, p.spring)), rev = N(gain(ac, 0.22));
  gIn.connect(gPk); gPk.connect(gLP); gLP.connect(gPan); gPan.connect(out);
  gPan.connect(gEcho); gEcho.connect(desk.echoIn); gPan.connect(gSpr); gSpr.connect(desk.springIn); gPan.connect(rev); rev.connect(send);
  const nz = N(ac.createBufferSource()), nBP = N(filter(ac, 'bandpass', 2200, 1.1)), nAmp = N(gain(ac, 0));
  nz.buffer = noise(ac, 'white'); nz.loop = true;
  nz.connect(nBP); nBP.connect(nAmp); nAmp.connect(gIn);
  // organ tone → pan → out · echo · reverb, + the vibrato-chorus
  const oIn = N(shaper(ac, 1.3, lite)), oLP = N(filter(ac, 'lowpass', p.oLp, 0.6)), oPan = N(panner(ac, p.oPan)), oEcho = N(gain(ac, p.echo.o));
  oIn.connect(oLP); oLP.connect(oPan); oPan.connect(out); oPan.connect(oEcho); oEcho.connect(desk.echoIn); oPan.connect(rev);
  const src = [nz];
  if (!lite) {
    const cD = N(ac.createDelay(0.02)), cW = N(gain(ac, 0.5)), cPan = N(panner(ac, -p.oPan)), lfo = N(osc(ac, 'sine', p.chorus.rate)), lfoG = N(gain(ac, p.chorus.ms / 1000));
    cD.delayTime.value = 0.004;
    oLP.connect(cD); cD.connect(cW); cW.connect(cPan); cPan.connect(out); cPan.connect(rev);
    lfo.connect(lfoG); lfoG.connect(cD.delayTime);
    src.push(lfo);
  }
  const bus = persistent(registry, tag, src, nodes, t0, 0.7);
  let voices = [], maxActive = 0, steals = 0, notes = 0;       // { vs: [registry voices], layers, midi, pad, off, end, rec }

  /** one layer of a note: an oscillator on `wave` and its gain into `into`, registered as its own voice */
  const layer = (wave, f, cents, into, t, end) => {
    const o = osc(ac, 'sine', f), g = gain(ac, 0);
    o.setPeriodicWave(wave); o.detune.value = cents;
    o.connect(g); g.connect(into);
    o.start(t); o.stop(end);
    return { o, g, v: registry.add({ src: [o], all: [o, g], start: t, end, tag, out: g }) };
  };
  const cut = (x, t, tau = 0.01) => { for (const v of x.vs) registry.stop(v, t, tau); x.end = t; if (x.rec) x.rec[1] = Math.max(x.rec[0], t); };

  function note(t, midi, vel, dur, relTau, kind) {
    voices = voices.filter(x => x.end > t);
    const f = mtof(midi), off = t + Math.max(0.04, dur), pad = kind === 'pad';
    const s = pad ? clamp((dur / stepDur - 2.6) / 5.4, 0, 1) : 0;   // 0 a chop … 1 held for 8+ steps
    const atk = lerp(0.006, p.pad.atk, s), rel = pad ? Math.max(relTau, lerp(0.06, p.pad.rel, s)) : 0.02;
    const k = voices.findIndex(x => x.midi === midi);
    if (k >= 0 && pad && voices[k].pad && t < voices[k].off + 0.06 && voices[k].off > ac.currentTime + 0.02) {
      // a held organ note struck again where it ends (a chord held over the bar line, a common tone): it ties
      const y = voices[k], o2 = Math.max(off, y.off), end = o2 + rel * 7 + 0.05;
      for (const { o, g } of y.layers) { g.gain.cancelScheduledValues(y.off); g.gain.setTargetAtTime(0, o2, rel); try { o.stop(end); } catch { /* ended */ } }
      for (const v of y.vs) v.end = end;
      y.off = o2; y.end = o2 + rel * 4;
      if (y.rec) y.rec[1] = y.end;
      return;
    }
    if (k >= 0) cut(voices.splice(k, 1)[0], t);                   // the same key again: its last note lets go (10 ms)
    else if (voices.length >= cap) { cut(voices.shift(), t); steals++; }
    const layers = [];
    if (kind === 'chop' && gtrOn) {
      const A = 0.52 * Math.pow(vel, 1.2) * gMix * L, end = off + 0.12;
      const y = layer(W.gtr, f, r() * 6 - 3, gIn, t, end), g = y.g.gain;
      g.setValueAtTime(0, t); g.linearRampToValueAtTime(A, t + 0.002);
      g.setTargetAtTime(0, t + 0.002, p.gDecay); g.setTargetAtTime(0, off, 0.012);
      layers.push(y);
    }
    if (kind !== 'chop' || orgOn) {
      const A = 0.61 * Math.pow(vel, 1.3) * (kind === 'chop' ? oMix : kind === 'bub' ? 0.85 : 0.36) * L, end = off + rel * 7 + 0.05;
      const y = layer(W.organ, f, r() * 8 - 4, oIn, t, end), g = y.g.gain;
      g.setValueAtTime(0, t); g.setTargetAtTime(A, t, pad ? atk : 0.0025);
      g.setTargetAtTime(A * lerp(0.78, 0.9, s), t + lerp(0.02, 0.4, s), lerp(0.25, 1.2, s));
      g.setTargetAtTime(0, off, rel);
      layers.push(y);
    }
    const end = Math.max(...layers.map(y => y.v.end)), x = { vs: layers.map(y => y.v), layers, midi, pad, off, end: pad ? off + rel * 4 : Math.min(end, off + 0.1), rec: null };
    if (log) { x.rec = [t, x.end]; log.push(x.rec); }
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  return {
    note,
    /** a chop strums up over `strum` (the top +6 %); an organ chord sounds together (4 ms apart); see above for the throw */
    chord(t, midis, vel, dur, strum = 0.01, relTau = 0.08) {
      const n = midis.length, kind = dur >= 2.6 * stepDur ? 'pad' : n === 1 ? 'bub' : 'chop', thrown = kind === 'chop' && vel >= 0.999, v = thrown ? p.vel : vel;
      if (kind === 'chop' && gtrOn) {
        hold(gLP.frequency, t);
        gLP.frequency.setTargetAtTime(p.gLp * p.pluck, t, 0.002); gLP.frequency.setTargetAtTime(p.gLp, t + 0.006, 0.05);
        nAmp.gain.setTargetAtTime(0.12 * v * gMix * L, t, 0.0008); nAmp.gain.setTargetAtTime(0, t + 0.003, 0.012);
      }
      if (thrown) {
        for (const [g, base] of [[gEcho.gain, p.echo.g], [oEcho.gain, p.echo.o]]) {
          g.setTargetAtTime(0.95, t - 0.004, 0.002); g.setTargetAtTime(base, t + Math.min(dur, 0.25), 0.04);
        }
        desk.throw(t);
      }
      midis.forEach((m, k) => {
        const dt = kind === 'pad' ? k * 0.004 : n > 1 ? (k * strum) / (n - 1) : 0;
        note(t + dt, m, k === n - 1 && n > 1 ? Math.min(1, v * 1.06) : v, dur - dt, relTau, kind);
      });
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); desk.dispose(t); },
  };
}

/* ---------------- bass: the riddim's round sub ---------------- */
/** p = plan.dub.bass: { tri, drive, lp, level }. Sine + triangle (p.tri .18–.32) → tanh (k p.drive 1.5–2.2, unity for small
 *  signals: the 3rd and 5th harmonics that carry it on phones) → LP p.lp (420–620 Hz, Q .6) → amp: attack τ 7 ms (round, no
 *  click) to .56·level·v, easing to .8 of it (τ .45 s), release τ 40 ms. A slide comes up from 2 semitones below (τ 35 ms). A
 *  note starts from wherever the last one is (legato lines don't dip to zero). */
export function createDubBass(ac, out, p, { registry, tag, t0, lite = false }) {
  const sine = osc(ac, 'sine', 55), tri = osc(ac, 'triangle', 55), triG = gain(ac, p.tri), drive = gain(ac, 1 / (1 + p.tri));
  const sh = shaper(ac, p.drive, lite), lpf = filter(ac, 'lowpass', p.lp, 0.6), amp = gain(ac, 0);
  sine.connect(drive); tri.connect(triG); triG.connect(drive); drive.connect(sh); sh.connect(lpf); lpf.connect(amp); amp.connect(out);
  const P = persistent(registry, tag, [sine, tri], [sine, tri, triG, drive, sh, lpf, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 1.12 * p.level * vel * LEVELS.bass * DUB_LEVELS.bass;
      for (const prm of [sine.frequency, tri.frequency, amp.gain]) hold(prm, t);
      for (const o of [sine, tri]) {
        if (slide) { o.frequency.setValueAtTime(f * Math.pow(2, -2 / 12), t); o.frequency.setTargetAtTime(f, t, 0.035); }
        else o.frequency.setValueAtTime(f, t);
      }
      amp.gain.setTargetAtTime(A, t, 0.007);
      amp.gain.setTargetAtTime(0.8 * A, t + 0.03, 0.45);
      amp.gain.setTargetAtTime(0, t + Math.max(0.06, dur), 0.04);
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- kit: a soft one-drop kit ---------------- */
/** kit = plan.drums.kit: { kickF0, kickF1, kickDecay, snareF, snareDecay, rimF, hatHP, hatClosed, openDecay, tomF0, tomF1, lp,
 *  echo: { rim, snare, tom }, spring }; plan.dub.throws: the bars whose backbeat is thrown.
 *  Every hit on an "and" (step 2, 6, 10 or 14, found from the event's time and bar) comes AND of a step late plus the
 *  engine's swing of a fractional step: the swung 8ths the comping cells and the bass line play too.
 *  kick: sine kickF0 (92–112) → kickF1 (47–54 Hz), τ 45 ms, .5v, decay τ kickDecay (.14–.25 s by energy: round and boomy)
 *        + a felt thump (noise → LP 600, .3v, τ 8 ms); no sends
 *  rim (the cross-stick): sine rimF (480–580) + triangle 2.3·rimF (.35) → BP 1.3 kHz (Q .9), .75v, τ 16 ms, pan −.12
 *  snare (a tight, high reggae snare): triangle 240 → 205 Hz (τ 25 ms) .26v τ 50 ms + noise → BP snareF (2.2–3 kHz, Q .7)
 *        .5v τ snareDecay (.07–.1 s; ghost 35 ms), centred
 *  hat: noise → HP hatHP (6.5–8.5k), .2v, closed τ hatClosed (15–24 ms), open τ openDecay (.16–.24 s), pan +.22
 *  tom: sine tomF0 (190–230) → tomF1 (140–165 Hz), τ 45 ms, .4v, τ .16 s, centred · shaker: the hat's noise, .1v, τ 28 ms,
 *        pan −.25
 *  Sends: rim / snare / tom → the echo (kit.echo: .2–.32 / .18–.3 / .3: a little, so a throw stands out), rim + snare → the
 *  spring (kit.spring), all three → reverb .22. All through the bus LP kit.lp. A throw: the first rim or snare from step 8 on
 *  in a throw bar goes into the echo at .95 (for 0.12 s) and swells it. */
export function createDubKit(ac, out, send, desk, plan, { registry, tag, t0, stepDur }) {
  const kit = plan.drums.kit, sd = stepDur, barDur = 16 * sd, L = LEVELS.kit * DUB_LEVELS.kit, nodes = [], N = x => (nodes.push(x), x);
  const andShift = AND * sd + swing(2 + AND, sd, plan.swing), lag = plan.snareLag, throws = new Set(plan.dub.throws);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5)), sprS = N(gain(ac, kit.spring)), revS = N(gain(ac, 0.22));
  bus.connect(out); sprS.connect(desk.springIn); revS.connect(send);
  /** a voice's gate → pan (null: centred) → the bus; with `echo`, sends to the echo (its own gain, for throws) and the reverb */
  const amp = (pan, echo = 0) => {
    const a = N(gain(ac, 0)), pn = pan == null ? a : N(panner(ac, pan));
    if (pn !== a) a.connect(pn);
    pn.connect(bus);
    if (!echo) return { a, pn };
    const e = N(gain(ac, echo));
    pn.connect(e); e.connect(desk.echoIn); pn.connect(revS);
    return { a, pn, e, base: echo };
  };
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kLP = N(filter(ac, 'lowpass', 600, 0.7)), kThump = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); src.connect(kLP); kLP.connect(kThump); kThump.connect(bus);
  const r1 = N(osc(ac, 'sine', kit.rimF)), r2 = N(osc(ac, 'triangle', 2.3 * kit.rimF)), rg2 = N(gain(ac, 0.35)), rBP = N(filter(ac, 'bandpass', 1300, 0.9)), rim = amp(-0.12, kit.echo.rim);
  r1.connect(rBP); r2.connect(rg2); rg2.connect(rBP); rBP.connect(rim.a); rim.pn.connect(sprS);
  const sOsc = N(osc(ac, 'triangle', 205)), sTone = N(gain(ac, 0)), sBP = N(filter(ac, 'bandpass', kit.snareF, 0.7)), sn = amp(null, kit.echo.snare);
  sOsc.connect(sTone); sTone.connect(sn.a); src.connect(sBP); sBP.connect(sn.a); sn.pn.connect(sprS);
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hat = amp(0.22), sh = amp(-0.25);
  src.connect(hHP); hHP.connect(hat.a); hHP.connect(sh.a);
  const tOsc = N(osc(ac, 'sine', kit.tomF1)), tom = amp(null, kit.echo.tom);
  tOsc.connect(tom.a);
  const P = persistent(registry, tag, [src, kOsc, r1, r2, sOsc, tOsc], nodes, t0, 1.9);
  const hit = (prm, peak, t, atk, rel, tau) => { prm.setTargetAtTime(peak, t, atk); prm.setTargetAtTime(0, t + rel, tau); };
  /** the event's grid step (its time within its bar, the snare's lag taken off) */
  const stepOf = (e, lagged) => (e && Number.isFinite(e.t) && e.bar != null ? (e.t - e.bar * barDur - (lagged ? lag : 0)) / sd : null);
  const late = (e, lagged = false) => {
    const s = stepOf(e, lagged), k = Math.round(s ?? -1);
    return s != null && mod(k, 4) === 2 && Math.abs(s - k) < 0.35 ? andShift : 0;
  };
  let thrownBar = -1;
  /** a rim or snare from step 8 on in a throw bar: into the echo at .95 for 0.12 s, the loop swelling */
  const maybeThrow = (v, t, e) => {
    const s = stepOf(e, true);
    if (s == null || !throws.has(e.bar) || thrownBar === e.bar || s < 7.6) return;
    thrownBar = e.bar;
    v.e.gain.setTargetAtTime(0.95, t - 0.003, 0.002); v.e.gain.setTargetAtTime(v.base, t + 0.12, 0.05);
    desk.throw(t);
  };
  return {
    kick(t, v, e) {
      t += late(e);
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.045);
      hit(kAmp.gain, 0.5 * v * L, t, 0.002, 0.008, kit.kickDecay);
      hit(kThump.gain, 0.3 * v * L, t, 0.001, 0.003, 0.008);
    },
    rim(t, v, e) {
      t += late(e, true);
      maybeThrow(rim, t, e);
      hit(rim.a.gain, 0.75 * v * L, t, 0.0006, 0.002, 0.016);
    },
    snare(t, v, ghost = false, e) {
      t += late(e, true);
      if (!ghost) maybeThrow(sn, t, e);
      sOsc.frequency.setValueAtTime(240, t); sOsc.frequency.setTargetAtTime(205, t, 0.025);
      hit(sTone.gain, 0.26 * v * L, t, 0.001, 0.004, 0.05);
      hit(sn.a.gain, 0.5 * v * L, t, 0.0012, 0.004, ghost ? 0.035 : kit.snareDecay);
    },
    hat(t, v, open = false, e) { t += late(e); hit(hat.a.gain, 0.2 * v * L, t, 0.0008, 0.003, open ? kit.openDecay : kit.hatClosed); },
    tom(t, v, e) {
      t += late(e);
      tOsc.frequency.setValueAtTime(kit.tomF0, t); tOsc.frequency.setTargetAtTime(kit.tomF1, t, 0.045);
      hit(tom.a.gain, 0.4 * v * L, t, 0.001, 0.005, 0.16);
    },
    shaker(t, v, e) { t += late(e); hit(sh.a.gain, 0.1 * v * L, t, 0.004, 0.008, 0.028); },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- lead: a melodica, mono, into the dub echo ---------------- */
/** lead = plan.lead ({ pan, timbre 'reed' | 'soft' }), p = plan.dub.lead: { detune (¢), bp (Hz), breath, trem, echo }.
 *  A 36 % pulse + a saw (.3, +p.detune ¢: the reed's beating) → peaking p.bp +5 dB (Q 1.2: the nasal free reed) →
 *  LP 3.2 kHz (2.5k soft) → amp → breath tremolo (5.2 Hz ±p.trem from .35 s) → pan; breath noise → BP 1.9 kHz (Q .9) → a gain
 *  following the note at p.breath (×2.5 for the first 40 ms: the puff). Amplitude .55v: attack τ 22 ms with a −14 ¢ scoop
 *  (τ 45 ms), a swell to ×1.08 (τ .3 s), release τ 70 ms. A glide (legato) note only moves the pitch (τ 14 ms), no new
 *  attack. Sends: the dub echo p.echo, reverb .35. */
export function createMelodica(ac, out, send, desk, lead, p, { registry, tag, t0 }) {
  const nodes = [], N = x => (nodes.push(x), x), W = waves(ac), soft = lead.timbre === 'soft';
  const o1 = N(osc(ac, 'sine', 440)), o2 = N(osc(ac, 'sawtooth', 440)), g2 = N(gain(ac, 0.3));
  const pk = N(filter(ac, 'peaking', p.bp, 1.2, 5)), lp = N(filter(ac, 'lowpass', soft ? 2500 : 3200, 0.7)), amp = N(gain(ac, 0));
  const trem = N(gain(ac, 1)), lfo = N(osc(ac, 'sine', 5.2)), lfoG = N(gain(ac, 0)), pan = N(panner(ac, lead.pan || 0));
  const nz = N(ac.createBufferSource()), nBP = N(filter(ac, 'bandpass', 1900, 0.9)), nAmp = N(gain(ac, 0));
  const echo = N(gain(ac, p.echo)), rev = N(gain(ac, 0.35));
  o1.setPeriodicWave(W.reed); o2.detune.value = p.detune;
  nz.buffer = noise(ac, 'white'); nz.loop = true;
  o1.connect(pk); o2.connect(g2); g2.connect(pk); pk.connect(lp); lp.connect(amp);
  nz.connect(nBP); nBP.connect(nAmp); nAmp.connect(amp);
  amp.connect(trem); trem.connect(pan); lfo.connect(lfoG); lfoG.connect(trem.gain);
  pan.connect(out); pan.connect(echo); echo.connect(desk.echoIn); pan.connect(rev); rev.connect(send);
  const P = persistent(registry, tag, [o1, o2, lfo, nz], nodes, t0, 2.6), Lv = DUB_LEVELS.lead;
  let lastOff = -1;
  return {
    note(t, midi, vel, dur, glide = false) {
      dur = Math.max(0.08, dur);
      const f = mtof(midi), A = 0.55 * vel * Lv, legato = glide && t <= lastOff + 0.05;
      for (const prm of [o1.frequency, o2.frequency, o1.detune, amp.gain, nAmp.gain, lfoG.gain]) hold(prm, t);
      for (const o of [o1, o2]) { if (legato) o.frequency.setTargetAtTime(f, t, 0.014); else o.frequency.setValueAtTime(f, t); }
      if (!legato) {
        o1.detune.setValueAtTime(-14, t); o1.detune.setTargetAtTime(0, t, 0.045);
        amp.gain.setTargetAtTime(A, t, 0.022);
        nAmp.gain.setTargetAtTime(2.5 * p.breath, t, 0.01); nAmp.gain.setTargetAtTime(p.breath, t + 0.04, 0.06);
      } else amp.gain.setTargetAtTime(A, t, 0.03);
      amp.gain.setTargetAtTime(1.08 * A, t + 0.08, 0.3);
      amp.gain.setTargetAtTime(0, t + dur, 0.07);
      nAmp.gain.setTargetAtTime(0, t + dur, 0.05);
      lfoG.gain.setTargetAtTime(0, t, 0.02);
      if (dur > 0.5) lfoG.gain.setTargetAtTime(p.trem, t + 0.35, 0.15);
      lastOff = t + dur;
    },
    dispose(t) { P.dispose(t); },
  };
}
